import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight, ChevronLeft, Phone, AtSign } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { toast } from '../hooks/use-toast';

const SIGNUP_DRAFT_KEY = 'tbt_signup_draft_v1';
const JOURNEY_KEY = 'tbt_guest_journey_v1';
const readJourney = () => { try { return JSON.parse(sessionStorage.getItem(JOURNEY_KEY) || '{}'); } catch { return {}; } };

// Short, personal lines built from the visitor's onboarding answers and challenge results
const buildInsights = (j) => {
  const a = j.answers || {};
  const out = [];
  if (j.plan) out.push({ t: j.plan === 'free' ? 'Free Plan selected' : `Customising your ${j.plan[0].toUpperCase()}${j.plan.slice(1)} plan…`, d: j.plan === 'free' ? '5 puzzles a day, upgrade anytime.' : '7-day free trial · autopay set up right after this step.' });
  if (['6 \u2013 8 hours', 'More than 8 hours'].includes(a.screen_time)) out.push({ t: 'Screen time is a lot!', d: 'A few focused minutes a day will balance it out.' });
  else if (a.screen_time) out.push({ t: 'Healthy screen habits', d: "We'll keep sessions short and sharp." });
  if (a.primary_goal) out.push({ t: `Goal locked: ${a.primary_goal}`, d: 'Your puzzles are tuned to it.' });
  if (a.training_frequency) out.push({ t: `Rhythm: ${a.training_frequency}`, d: "We'll remind you at the right pace." });
  const res = j.results || [];
  if (res.length) {
    const ok = res.filter((r) => r.correct).length;
    const weak = res.find((r) => !r.correct);
    out.push({ t: `You scored ${ok}/${res.length} in your brain check`, d: weak ? `We'll give ${weak.category} a little extra focus.` : 'Impressive — we will raise the challenge.' });
  }
  return out.slice(0, 5);
};

const Insights = ({ items }) => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (shown >= items.length) return undefined;
    const t = setTimeout(() => setShown((n) => n + 1), shown === 0 ? 150 : 650);
    return () => clearTimeout(t);
  }, [shown, items.length]);
  if (!items.length) return null;
  return (
    <ul className="mb-5 space-y-2" data-testid="signup-insights" aria-live="polite">
      {items.slice(0, shown).map((it) => (
        <li key={it.t} className="rounded-xl bg-[#2A1810] border border-[#F4C430]/30 px-3.5 py-2.5 animate-[fadeIn_.4s_ease-out]">
          <div className="text-sm text-[#F4C430] font-medium">{it.t}</div>
          <div className="text-xs text-[#B8A990]">{it.d}</div>
        </li>
      ))}
    </ul>
  );
};

const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen bg-[#2A1810] text-[#F5EDE0] flex flex-col p-4 relative overflow-hidden">
    <div className="absolute -top-40 -left-24 w-[520px] h-[520px] rounded-full bg-[#5a3d24]/40 blur-[120px] pointer-events-none" />
    <div className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-[#F4C430]/10 blur-[120px] pointer-events-none" />
    <div
      className="absolute inset-0 opacity-[0.05] pointer-events-none"
      style={{ backgroundImage: 'radial-gradient(#F4C430 1px, transparent 1px)', backgroundSize: '28px 28px' }}
    />
    {/* Back-to-home bar — always at top, no overlap with card */}
    <div className="relative z-10 max-w-md w-full mx-auto flex justify-start">
      <Link
        to="/"
        data-testid="auth-back-home"
        aria-label="Back to home"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#5a3d24] bg-[#3D2817]/60 backdrop-blur px-3 py-1.5 text-sm text-[#D9C9B3] hover:text-[#F4C430] hover:border-[#F4C430] transition-colors"
      >
        <ChevronLeft size={16} /> Back to home
      </Link>
    </div>
    <div className="relative flex-1 flex items-center justify-center py-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-5"><BrandLogo size="md" /></div>
        <div className="rounded-3xl bg-[#3D2817] border border-[#4a3221] p-6 sm:p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
          <h1 className="font-serif text-2xl sm:text-3xl leading-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-[#B8A990] text-sm">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-4 text-center text-sm text-[#B8A990]">{footer}</div>}
      </div>
    </div>
  </div>
);

const TextField = ({ icon: Icon, ...props }) => (
  <label className="block">
    <div className="relative">
      {Icon && <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8f7658]" />}
      <input
        {...props}
        className={`w-full rounded-xl bg-[#2A1810] border border-[#5a3d24] px-4 ${Icon ? 'pl-10' : ''} py-3 text-sm text-[#F5EDE0] placeholder:text-[#8f7658] focus:outline-none focus:border-[#F4C430] ${props.className || ''}`}
      />
    </div>
  </label>
);

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ identifier: '', password: '' });
  const [errs, setErrs] = useState({});
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    const id = form.identifier.trim();
    const looksLikeEmail = /.+@.+\..+/.test(id);
    const digits = id.replace(/\D/g, '');
    const looksLikeMobile = digits.length >= 8;
    if (!looksLikeEmail && !looksLikeMobile) next.identifier = 'Enter a valid email or mobile number';
    if (!form.password || form.password.length < 6) next.password = 'Password must be at least 6 characters';
    setErrs(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    try {
      await login({ identifier: id, password: form.password });
      navigate(loc.state?.from?.pathname || '/dashboard', { replace: true });
    } catch (err) {
      const code = err?.code;
      const msg = (err?.message || 'Something went wrong').toLowerCase();
      if (code === 'email_not_registered' || code === 'identifier_not_registered' ||
          (msg.includes('not') && (msg.includes('registered') || msg.includes('unknown')))) {
        setErrs({ identifier: err?.message || 'This account is not registered. Try signing up.' });
      } else if (code === 'incorrect_password' || msg.includes('password') || msg.includes('invalid')) {
        setErrs({ password: err?.message || 'Incorrect password. Please try again.' });
      } else {
        setErrs({ form: err?.message || 'Could not log in. Please check your connection and try again.' });
      }
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={<>Welcome back! <span className="italic text-[#F4C430]">Ready to train?</span></>}
      subtitle="Log in with your email or mobile number"
      footer={<>Don't have an account? <Link to="/auth/signup" className="text-[#F4C430] hover:underline">Sign up</Link></>}
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        <FieldWithError error={errs.identifier}>
          <TextField icon={AtSign} type="text" placeholder="Email or mobile number" value={form.identifier}
            onChange={(e) => { setForm({ ...form, identifier: e.target.value }); if (errs.identifier) setErrs({ ...errs, identifier: null }); }}
            data-testid="login-identifier"
            aria-invalid={!!errs.identifier} required autoComplete="username" inputMode="text" />
        </FieldWithError>
        <FieldWithError error={errs.password}>
          <div className="relative">
            <TextField icon={Lock} type={show ? 'text' : 'password'} placeholder="Password"
              value={form.password}
              onChange={(e) => { setForm({ ...form, password: e.target.value }); if (errs.password) setErrs({ ...errs, password: null }); }}
              data-testid="login-password"
              aria-invalid={!!errs.password} required autoComplete="current-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center text-[#8f7658] hover:text-[#F4C430]" aria-label="Toggle password visibility">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FieldWithError>
        {errs.form && (
          <div className="rounded-xl border border-red-400/50 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="login-form-error">
            {errs.form}
          </div>
        )}
        <div className="flex justify-end">
          <Link to="/auth/forgot" className="text-xs text-[#B8A990] hover:text-[#F4C430]">Forgot password?</Link>
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12">
          {loading ? 'Logging in…' : 'Log In'} <ArrowRight size={16} className="ml-1" />
        </Button>
      </form>
    </AuthShell>
  );
};

const FieldWithError = ({ error, children }) => (
  <div>
    {children}
    {error && (
      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-red-300" role="alert">
        <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
        <span>{error}</span>
      </div>
    )}
  </div>
);

export const Signup = () => {
  const { signup, completeOnboarding, refresh } = useAuth();
  const navigate = useNavigate();
  const [journey] = useState(readJourney);
  const insights = buildInsights(journey);
  const [form, setFormRaw] = useState(() => {
    try { return { name: journey.name || '', email: '', mobile: journey.mobile || '', ...JSON.parse(sessionStorage.getItem(SIGNUP_DRAFT_KEY) || '{}'), password: '' }; }
    catch { return { name: '', email: '', mobile: '', password: '' }; }
  });
  // Remember typed details (never the password) so a retry after a failed payment needs no retyping
  const setForm = (next) => {
    setFormRaw(next);
    const { password, ...rest } = next;
    sessionStorage.setItem(SIGNUP_DRAFT_KEY, JSON.stringify(rest));
  };
  const [errs, setErrs] = useState({});
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name';
    if (!form.email.match(/.+@.+\..+/)) next.email = 'Please enter a valid email address';
    const digits = (form.mobile || '').replace(/\D/g, '');
    if (!digits) next.mobile = 'Please enter your mobile number';
    else if (digits.length < 8) next.mobile = 'Mobile number looks too short';
    else if (digits.length > 15) next.mobile = 'Mobile number looks too long';
    if (!form.password || form.password.length < 6) next.password = 'Create a password with at least 6 characters';
    setErrs(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    try {
      // Send +CC-prefixed mobile if user didn't add one (default +91 India)
      const mobileNorm = form.mobile.trim().startsWith('+')
        ? form.mobile.trim()
        : `+91${digits}`;
      await signup({ ...form, mobile: mobileNorm });
      // Guest journey (funnel → onboarding → plan) finishes here; otherwise start onboarding
      if (journey.answers) {
        await completeOnboarding({ ...journey.answers, freeChallenges: journey.results || [] });
        if (journey.plan === 'free') {
          await api.chooseFree();
          sessionStorage.removeItem(JOURNEY_KEY);
          await refresh();
          navigate('/dashboard', { replace: true });
        } else if (journey.plan) {
          navigate(`/choose-plan?checkout=${journey.plan}`, { replace: true });
        } else {
          navigate('/choose-plan', { replace: true });
        }
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      const code = err?.code;
      const msg = (err?.message || '').toLowerCase();
      if (code === 'mobile_already_registered' || msg.includes('mobile')) {
        setErrs({ mobile: err?.message || 'This mobile number is already registered. Please log in.', loginHint: true });
      } else if (code === 'email_already_registered' || msg.includes('already') || msg.includes('exists') || msg.includes('registered')) {
        setErrs({ email: 'This email is already registered. Please log in.', loginHint: true });
      } else if (msg.includes('password')) {
        setErrs({ password: err.message });
      } else {
        setErrs({ form: err?.message || 'Could not create your account. Please check your connection and try again.' });
      }
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={<>Create your <span className="italic text-[#F4C430]">Brain Theory</span> account</>}
      subtitle={journey.answers ? 'Last step — save your brain profile and start training' : 'Create your account, then a quick brain profile'}
      footer={<>Already have an account? <Link to="/auth/login" className="text-[#F4C430] hover:underline">Log in</Link></>}
    >
      <Insights items={insights} />
      <form onSubmit={submit} className="space-y-3" noValidate>
        <FieldWithError error={errs.name}>
          <TextField type="text" placeholder="Your name" value={form.name}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errs.name) setErrs({ ...errs, name: null }); }}
            data-testid="signup-name"
            aria-invalid={!!errs.name} required autoComplete="name" />
        </FieldWithError>
        <FieldWithError error={errs.email}>
          <TextField icon={Mail} type="email" placeholder="Email" value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errs.email) setErrs({ ...errs, email: null }); }}
            data-testid="signup-email"
            aria-invalid={!!errs.email} required autoComplete="email" />
        </FieldWithError>
        <FieldWithError error={errs.mobile}>
          <TextField icon={Phone} type="tel" placeholder="Mobile number (e.g. +91 98765 43210)" value={form.mobile}
            onChange={(e) => { setForm({ ...form, mobile: e.target.value }); if (errs.mobile) setErrs({ ...errs, mobile: null }); }}
            data-testid="signup-mobile"
            aria-invalid={!!errs.mobile} required autoComplete="tel" inputMode="tel" />
        </FieldWithError>
        <FieldWithError error={errs.password}>
          <div className="relative">
            <TextField icon={Lock} type={show ? 'text' : 'password'} placeholder="Create a password (min 6 chars)"
              value={form.password}
              onChange={(e) => { setForm({ ...form, password: e.target.value }); if (errs.password) setErrs({ ...errs, password: null }); }}
              data-testid="signup-password"
              aria-invalid={!!errs.password} required autoComplete="new-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center text-[#8f7658] hover:text-[#F4C430]" aria-label="Toggle password visibility">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </FieldWithError>
        {errs.loginHint && (
          <div className="rounded-xl border border-[#F4C430]/40 bg-[#3D2817] px-4 py-3 text-sm text-[#F5EDE0]" data-testid="signup-login-hint">
            You already have an account. <Link to="/auth/login" className="text-[#F4C430] font-medium hover:underline" data-testid="signup-login-link">Log in to continue →</Link>
          </div>
        )}
        {errs.form && (
          <div className="rounded-xl border border-red-400/50 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="signup-form-error">
            {errs.form}
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12">
          {loading ? 'Creating account…' : 'Create Account'} <ArrowRight size={16} className="ml-1" />
        </Button>
      </form>
    </AuthShell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.match(/.+@.+\..+/)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address' });
      return;
    }
    // Fire and forget — backend always returns OK for privacy
    try {
      const { api } = await import('../api');
      await api.forgot({ email });
    } catch {}
    setSent(true);
    toast({ title: 'Reset link sent!', description: 'Check your inbox for a password reset link' });
  };

  return (
    <AuthShell
      title={<>Reset your <span className="italic text-[#F4C430]">password</span></>}
      subtitle="We will send a reset link to your email"
      footer={<Link to="/auth/login" className="text-[#F4C430] hover:underline">Back to login</Link>}
    >
      {sent ? (
        <div className="text-center py-4">
          <div className="mx-auto h-14 w-14 rounded-full grid place-items-center bg-[#F4C430]/20 border border-[#F4C430]/50">
            <Mail className="text-[#F4C430]" size={24} />
          </div>
          <p className="mt-4 text-[#F5EDE0]">Check <span className="text-[#F4C430]">{email}</span> for your reset link</p>
          <p className="mt-1 text-[#B8A990] text-sm">It usually arrives within a minute</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <TextField icon={Mail} type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} required />
          <Button type="submit" className="w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12">
            Send Reset Link <ArrowRight size={16} className="ml-1" />
          </Button>
        </form>
      )}
    </AuthShell>
  );
};
